<template>
  <div class="validation-page-wrapper">
    <div class="validation-page-content">
      <h1
        class="validation-page-content__title u-typography-helvetica u-text-display u-text-display--m"
      >
        {{ $t('validationPage.title', [patientName]) }}
      </h1>

      <section class="validation-page-content__section order-status-section section">
        <div class="order-status-section__order-status order-status">
          <div class="order-status__message flex--align-center">
            <span class="custom-icon icon-check-circle"></span>
            <p class="u-typography-helvetica u-text u-text--l">
              {{ $t('checkout.orderPlacedSuccessfully') }}
            </p>
          </div>
          <p class="order-status__order-number u-typography-helvetica u-text u-text--m">
            {{ $t('validationPage.orderNumberLabel') }}: {{ orderId }}
          </p>
          <i18n
            class="order-status__delivery-info u-typography-helvetica u-text u-text--m"
            tag="p"
            path="validationPage.deliveryInfoText"
          >
            <span class="text-bold">{{ patientEmail }}</span>
          </i18n>
        </div>

        <p class="order-status-section__delivery-details u-typography-helvetica u-text u-text--m">
          {{ deliveryInfoDetails }}
        </p>
      </section>

      <section
        v-if="isRenew"
        class="validation-page-content__section renew-treatment-section section--with-border"
      >
        <slot name="renewSection"></slot>
      </section>

      <section class="validation-page-content__section doctor-quote-section section--with-border">
        <span
          class="doctor-quote-section__title u-typography-helvetica u-text u-text--m text-bold"
          >{{ $t('common.label.yourDoctor') }}</span
        >
        <div class="doctor-quote-section__content quote-section-content">
          <introduction-reference
            class="quote-section-content__introduction-reference"
            :content="{ textKey: 'validationPage.doctorQuote' }"
          />
          <div class="quote-section-content__doctor-info doctor-info">
            <span class="doctor-info__doctor-signature">{{ doctorFullName }}</span>
            <div v-if="doctorWebsite" class="doctor-info__doctor-website doctor-website">
              <span class="custom-icon icon-globe"></span>
              <a
                :href="doctorWebsite"
                class="doctor-website__link"
                target="__blank"
                rel="noopener"
                >{{ $t('checkout.doctorsPersonalSite') }}</a
              >
            </div>
          </div>
        </div>
      </section>

      <section class="validation-page-content__section order-summary-section section">
        <header class="section__header section-header flex--space-between--start">
          <h2 class="section-header__title">{{ $t('checkout.orderSummary') }}</h2>

          <i18n
            class="section-header__order-date u-typography-helvetica u-text u-text--m"
            tag="p"
            path="checkout.orderDate"
          >
            <span place="dateOfSale">{{ paymentDay }}</span>
          </i18n>
        </header>

        <slot name="orderSummary"></slot>
      </section>

      <div class="shipping-sections-wrapper">
        <section
          class="validation-page-content__section shipping-info-section section section--with-border"
        >
          <header class="section__header section-header">
            <h2 class="section-header__title">{{ $t('checkout.shippingInfo') }}</h2>
          </header>
          <table class="address-data">
            <tr
              v-for="{ fieldLabel, fieldName } in $options.shippingAddress"
              :key="fieldName"
              class="address-data__field address-field"
              :class="`billing-${fieldName}`"
            >
              <td class="address-field__label u-typography-helvetica u-text u-text--link u-text--s">
                {{ $t(fieldLabel) }}
              </td>
              <td class="address-field__value u-typography-helvetica u-text u-text--s">
                {{ shippingAddressData[fieldName] }}
              </td>
            </tr>
          </table>
        </section>

        <section
          class="validation-page-content__section billing-info-section section section--with-border"
        >
          <header class="section__header section-header">
            <h2 class="section-header__title">{{ $t('checkout.billingInfo') }}</h2>
          </header>
          <table class="address-data">
            <tr
              v-for="{ fieldLabel, fieldName } in $options.billingAddress"
              :key="fieldName"
              class="address-data__field address-field"
              :class="`billing-${fieldName}`"
            >
              <td class="address-field__label u-typography-helvetica u-text u-text--link u-text--s">
                {{ $t(fieldLabel) }}
              </td>
              <td class="address-field__value u-typography-helvetica u-text u-text--s">
                {{ billingAddressData[fieldName] }}
              </td>
            </tr>
          </table>
        </section>
      </div>

      <section class="validation-page-content__section order-info-section section">
        <header class="section__header section-header order-info-section__header">
          <h2 class="section-header__title">{{ $t('checkout.orderInformation') }}</h2>
        </header>

        <slot name="orderInformation"></slot>
      </section>
    </div>
  </div>
</template>

<script>
import IntroductionReference from '@/components/common/IntroductionReference.vue';

import { parsePhone } from '@/utils/phone';
import { getFullShippingAddress } from '@/utils/shipping';
import { displayNameMixin } from '@/mixins/displayNameMixin';

const SHIPPING_ADDRESS_FIELDS_CONFIG = [
  {
    fieldLabel: 'checkout.input.shippingToLabel',
    fieldName: 'country'
  },
  {
    fieldLabel: 'checkout.form.fullName',
    fieldName: 'fullName'
  },
  {
    fieldLabel: 'checkout.address',
    fieldName: 'addressData'
  },
  {
    fieldLabel: 'common.input.phoneNumber.label',
    fieldName: 'phone'
  },
  {
    fieldLabel: 'checkout.handlingAndShippingLabel',
    fieldName: 'deliveryType'
  }
];

const BILLING_ADDRESS_FIELDS_CONFIG = [
  {
    fieldLabel: 'checkout.form.billingTo',
    fieldName: 'country'
  },
  {
    fieldLabel: 'checkout.form.fullName',
    fieldName: 'fullName'
  },
  {
    fieldLabel: 'checkout.address',
    fieldName: 'addressData'
  },
  {
    fieldLabel: 'checkout.paymentType',
    fieldName: 'paymentType'
  },
  {
    fieldLabel: 'checkout.sendEmailTo',
    fieldName: 'email'
  }
];

export default {
  name: 'ValidationPageTemplate',
  components: {
    IntroductionReference
  },
  mixins: [displayNameMixin],
  billingAddress: BILLING_ADDRESS_FIELDS_CONFIG,
  shippingAddress: SHIPPING_ADDRESS_FIELDS_CONFIG,
  props: {
    patientName: {
      type: String,
      default: ''
    },
    patientEmail: {
      type: String,
      default: ''
    },
    doctorFullName: {
      type: String,
      default: ''
    },
    doctorWebsite: {
      type: String,
      default: ''
    },
    orderId: {
      type: Number,
      default: null
    },
    country: {
      type: String,
      default: ''
    },
    deliveryDays: {
      type: Number,
      default: null
    },
    isRenew: {
      type: Boolean,
      default: false
    },
    paymentDay: {
      type: String,
      default: ''
    },
    shippingAddress: {
      type: Object,
      default: () => {}
    },
    billingAddress: {
      type: Object,
      default: () => {}
    },
    isSaudiArabia: {
      type: Boolean,
      default: false
    },
    isSerumSelected: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    treatmentPreparation() {
      return this.isSerumSelected
        ? this.$t('validationPage.serumWillBeFreshlyMixedText')
        : this.$t('validationPage.treatmentWillBePreparedText');
    },
    deliveryInfoDetails() {
      return this.$t('validationPage.deliveryInfoDetails', [
        this.treatmentPreparation,
        this.deliveryDays
      ]);
    },
    shippingAddressData() {
      return this.transformShippingAddress(this.shippingAddress);
    },
    billingAddressData() {
      return this.transformShippingAddress(this.billingAddress);
    }
  },
  methods: {
    transformShippingAddress(shippingAddress) {
      const { countryCode, firstName = '', lastName = '', phone, phoneIso } = shippingAddress;

      const fullName = `${firstName} ${lastName}`;

      return {
        ...this.shippingAddress,
        addressData: getFullShippingAddress(shippingAddress, this.isSaudiArabia),
        fullName,
        phone: this.getPhoneNumber(phoneIso, phone),
        country: this.getCountryDisplayName(countryCode),
        email: this.patientEmail
      };
    },
    getPhoneNumber(phoneIso, phone) {
      if (!phoneIso && !phone) {
        return '';
      }

      const { international } = parsePhone(phoneIso, phone);

      return international;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/colors';
@import '../../assets/scss/icon.scss';

.validation-page-wrapper {
  background-color: inherit;
}

.validation-page-content {
  min-height: 100%;
  padding: 58px 16px 0;
  color: var(--u-color-grey-900);

  &__title {
    margin-bottom: 24px;
    color: var(--u-color-graphite);
  }
}

.validation-page-content:lang(ar) {
  direction: rtl;
}

.renew-treatment-section {
  margin-bottom: 30px;
}

.order-status-section {
  margin-bottom: 30px;

  &__order-status {
    margin-bottom: 24px;
  }
}

.order-status {
  padding: 24px;
  background-color: var(--u-color-light-green);
  border-radius: 10px;

  &__order-number {
    color: var(--u-color-grey-600);
    margin-bottom: 9px;
  }

  &__message {
    margin-bottom: 9px;
  }
}

.doctor-quote-section {
  margin: 30px 0;
}

.quote-section-content {
  &__introduction-reference {
    margin: 16px 0;
  }
}

.doctor-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  &__doctor-signature {
    font-family: var(--u-font-family-Quentin);
    font-size: 21.2857px;
    line-height: 31px;
  }

  &__doctor-website {
    margin-top: 17px;
  }
}

.doctor-website {
  display: flex;
  align-items: center;

  &__link {
    margin-left: 8px;
    font-family: var(--u-font-family-Gilroy);
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.4px;
    text-decoration-line: underline;
    color: var(--u-color-graphite);
  }
}

.icon-check-circle {
  margin-right: 18px;
}

.icon-check-circle:lang(ar) {
  margin-right: 0;
  margin-left: 18px;
}

.shipping-sections-wrapper {
  margin-bottom: 30px;
}

.section {
  flex: 1;
  width: 100%;
  margin-bottom: 35px;

  &--with-border {
    padding: 24px;
    border: 1px solid var(--u-color-grey-200);
    border-radius: 10px;
  }
}

.section-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--u-color-grey-200);

  &__title {
    font-size: 20px;
    font-weight: 400;
    line-height: 32px;
    letter-spacing: 0.4px;
    font-family: var(--u-font-family-Helvetica);
    color: var(--u-color-graphite);
  }

  &__order-date {
    display: flex;
    flex-direction: column;
    color: var(--u-color-grey-500);
  }
}

.address-data {
  width: 100%;

  &__field {
    display: flex;
    justify-content: space-between;
  }
}

.address-field {
  display: flex;
  justify-content: space-between;
  margin-bottom: 25px;

  &__value {
    margin-left: 10px;
    text-align: right;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.order-info-section {
  &__header {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
}

@media (min-width: 600px) {
  .doctor-info {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;

    &__doctor-signature {
      margin-bottom: 0;
      margin-right: 32px;
    }
  }
}

@media (min-width: 960px) {
  .shipping-sections-wrapper {
    display: flex;
    justify-content: space-between;
  }

  .shipping-info-section {
    margin-right: 30px;
  }

  .shipping-info-section:lang(ar) {
    margin-right: 0;
    margin-left: 30px;
  }

  .validation-page-content {
    max-width: 1016px;
    padding-top: 100px;
    --product-description-max-width: 100%;
    margin: 0 auto;

    &__title {
      margin: 50px 0 24px;
    }
  }

  .section-header {
    &__title {
      font-size: 24px;
      line-height: 38px;
    }
  }

  .address-field {
    display: grid;
    grid-template-columns: 1fr 1fr;

    &__value {
      text-align: left;
      width: auto;
    }
  }

  .doctor-quote-section {
    &__content {
      padding-right: 60px;
    }

    &__content:lang(ar) {
      padding-right: 0;
      padding-left: 60px;
    }
  }

  .doctor-info {
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;

    &__doctor-signature {
      margin-bottom: 0;
      margin-right: 32px;
    }
  }

  .order-info-section {
    &__header {
      margin-bottom: 8px;
      padding-bottom: 16px;
      border-bottom: 1px solid var(--u-color-grey-200);
    }
  }
}
</style>
